import React, { useMemo } from "react";

import { Divider, Stack, Typography } from "@mui/material";

import { AccountDetails } from "@fartherfinance/frontend/api/Dashboard/requests/getClientDashboard";
import {
  PortfolioAssignment,
  TransformerId,
} from "@fartherfinance/frontend/api/Investment/types";

import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import { PLACEHOLDER_SIGN } from "@src/yellowstone/modules/shared";

import styles from "./PortfolioAssignmentSummary.module.css";

// This matches the constant used in TradingGroupsTableV2.tsx
const EquivalentSecurityTransformerId =
  "IPT_FARF-EQEQ-0000" as unknown as TransformerId;

interface Props {
  portfolioAssignment: PortfolioAssignment;
  accounts: AccountDetails[];
}

const PortfolioAssignmentSummary: React.FC<Props> = ({
  accounts,
  portfolioAssignment,
}) => {
  // Check if TLH (Tax-Loss Harvesting) is enabled
  const isTLHEnabled = useMemo(
    () =>
      portfolioAssignment.rebalancers.some(
        (reb) => reb.rebalancer === "TaxLossHarvestingOnlyRebalancer"
      ),
    [portfolioAssignment.rebalancers]
  );

  // Check if Equivalent Securities is enabled
  const isEquivalentSecurityEnabled = useMemo(() => {
    return portfolioAssignment.transformers.some(
      (transformer) =>
        transformer.transformerId === EquivalentSecurityTransformerId
    );
  }, [portfolioAssignment.transformers]);

  return (
    <Stack className={styles.container}>
      <Typography
        className={toClassName(
          styles.text,
          styles.textPrimary,
          styles.groupNameRow
        )}
      >
        {portfolioAssignment.name}
      </Typography>

      {accounts.map((account, index) => {
        const accountName = account.accountDetails.displayName;

        return (
          <Stack
            key={index}
            className={styles.accountRow}
            direction="row"
            justifyContent="space-between"
          >
            <Typography
              className={toClassName(styles.text, styles.textPrimary)}
            >
              {accountName ?? PLACEHOLDER_SIGN}
            </Typography>
          </Stack>
        );
      })}

      <Divider className={styles.divider} />

      <Stack direction="row" justifyContent="space-between">
        <Typography className={toClassName(styles.text, styles.textPrimary)}>
          Trading
        </Typography>
        <Typography className={toClassName(styles.text, styles.textSecondary)}>
          {portfolioAssignment.intent === "TradingOn" ? "On" : "Off"}
        </Typography>
      </Stack>
      <Stack direction="row" justifyContent="space-between">
        <Typography className={toClassName(styles.text, styles.textPrimary)}>
          Tax-loss harvesting
        </Typography>
        <Typography className={toClassName(styles.text, styles.textSecondary)}>
          {isTLHEnabled ? "On" : "Off"}
        </Typography>
      </Stack>
      <Stack direction="row" justifyContent="space-between">
        <Typography className={toClassName(styles.text, styles.textPrimary)}>
          Equivalent Securities
        </Typography>
        <Typography className={toClassName(styles.text, styles.textSecondary)}>
          {isEquivalentSecurityEnabled ? "On" : "Off"}
        </Typography>
      </Stack>
    </Stack>
  );
};

export default PortfolioAssignmentSummary;
